import React, { Component, type PropsWithChildren } from 'react';

import { isEqual } from 'lodash-es';

import { getOS } from '@iconik-io/core/src/os/get_os';
import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';

import {
  findAncestorId,
  findAncestorWithId,
} from '../../components/search_items/utils';
import type { ObjectWithId } from '../../types/common';
import {
  targetElement,
  targetHtmlOrSvgElement,
} from '../../utils/event_target';
import { getScrollableParent } from '../../utils/get_scrollable_parent';
import type { SelectableItemsSelectionMode } from './types';

type SelectionMode = SelectableItemsSelectionMode;

const ignoredElements = new Set(['BUTTON']);

const anyAncestorIsOfType = (
  leaf: Element,
  parent: Element,
  tagNames: Set<string>,
): boolean => {
  let element: Element | null = leaf;
  while (element !== null && element !== parent) {
    if (tagNames.has(element.tagName)) {
      return true;
    }
    element = element.parentElement;
  }
  return tagNames.has(parent.tagName);
};

export type Props<Item extends ObjectWithId> = {
  className?: string;
  multiselect?: boolean;
  items: Item[];
  isSelectAllAllowed?: boolean;
  isSelectionLocked?: boolean;
  ignoreIds?: string[];
  selectedIds?: string[];
  onNavigateToItem?: (id: string) => void;
  onSelectIds?: (ids: string[]) => void;
  aria?: AriaProps;
  onSelectionModeChange?: (mode: SelectionMode) => void;
  onEscKeyPress?: () => void;
  idKey?: string;
};

class SelectableItems<Item extends ObjectWithId> extends Component<
  PropsWithChildren<Props<Item>>
> {
  static defaultProps = {
    multiselect: true,
    isSelectAllAllowed: true,
    isSelectionLocked: false,
    idKey: 'id',
  };

  rootRef = React.createRef<HTMLDivElement>();

  selectedIds = new Set<string>();

  shiftSelectStartId: string | null = null;

  // Flag to track selection and prevent double selection in mouse down and click handlers.
  skipOnClickSelect = false;

  setSelectedIds = (newSelectedIds: string[]): void => {
    const { ignoreIds = [] } = this.props;
    const ids = newSelectedIds.filter((id) => !ignoreIds.includes(id));
    this.selectedIds = new Set(ids);
    this.props.onSelectIds?.(ids);
  };

  setSelectedRows = (
    newSelectedIds: string[],
    preventUpdateShiftStart = false,
  ): void => {
    if (!preventUpdateShiftStart) {
      if (newSelectedIds.length) {
        this.shiftSelectStartId = newSelectedIds[newSelectedIds.length - 1];
      } else {
        this.shiftSelectStartId = null;
      }
    }
    this.setSelectedIds(newSelectedIds);
  };

  getSelectionMode = (event: React.MouseEvent): SelectionMode => {
    const { shiftKey, metaKey, ctrlKey } = event.nativeEvent;

    const os = getOS();
    const holdingCtrl =
      (os === 'MacOS' && metaKey) || (os !== 'MacOS' && ctrlKey);
    if (holdingCtrl && this.props.multiselect) {
      return 'ctrl';
    }

    if (shiftKey && this.props.multiselect && this.selectedIds.size > 0) {
      return 'shift';
    }

    return 'common';
  };

  handleMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target = targetHtmlOrSvgElement(event.nativeEvent.target);
    if (!target || target.dataset.menu) {
      return;
    }
    const ancestor = findAncestorWithId(target);
    if (!ancestor) {
      return;
    }

    const isIgnoredElement = anyAncestorIsOfType(
      target,
      ancestor,
      ignoredElements,
    );
    if (isIgnoredElement) {
      return;
    }

    const id = ancestor.getAttribute('data-id');
    if (!id) {
      return;
    }

    const selectionMode = this.getSelectionMode(event);
    this.props.onSelectionModeChange?.(selectionMode);
    switch (selectionMode) {
      case 'common':
        if (!this.selectedIds.has(id)) {
          this.setSelectedRows([id]);
          this.skipOnClickSelect = true;
        }
        break;
      case 'shift':
        this.handleShiftSelect(id);
        break;
      case 'ctrl':
        this.handleCtrlSelect(id);
        break;
      default:
        break;
    }
  };

  handleCtrlSelect = (id: string): void => {
    if (!this.selectedIds.has(id)) {
      this.selectRow(id);
    } else {
      this.unselectRow(id);
    }
  };

  handleShiftSelect = (toId: string): void => {
    const { items, idKey = 'id' } = this.props;
    let newSelectedIds: string[] = [];
    const startIndex = items.findIndex(
      (item) => item[idKey] === this.shiftSelectStartId,
    );
    const finishIndex = items.findIndex((item) => item[idKey] === toId);
    if (startIndex <= finishIndex) {
      newSelectedIds = this.props.items
        .filter((_item, index) => index >= startIndex && index <= finishIndex)
        .map((item) => item[idKey]);
    } else if (startIndex > finishIndex) {
      newSelectedIds = this.props.items
        .filter((_item, index) => index >= finishIndex && index <= startIndex)
        .map((item) => item[idKey]);
    }

    const selection = document.getSelection();
    if (selection) {
      selection.removeAllRanges();
    }

    this.setSelectedRows(newSelectedIds, true);
  };

  handleOnClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target = targetHtmlOrSvgElement(event.nativeEvent.target);
    if (!target) {
      return;
    }

    const ancestor = findAncestorWithId(target);
    if (!ancestor) {
      return;
    }

    const id = ancestor.getAttribute('data-id');
    if (!id) {
      return;
    }

    if (target.tagName === 'A' && this.props.onNavigateToItem) {
      this.props.onNavigateToItem(id);
    }

    if (id === target.dataset.menu) {
      this.handleContextMenuCall(event);
      return;
    }

    const isIgnoredElement = anyAncestorIsOfType(
      target,
      ancestor,
      ignoredElements,
    );
    if (isIgnoredElement) {
      return;
    }

    const selectionMode = this.getSelectionMode(event);
    if (selectionMode === 'common') {
      if (this.skipOnClickSelect) {
        this.skipOnClickSelect = false;
        return;
      }
      this.setSelectedRows([id]);
    }
  };

  unselectRow = (unselectId: string): void => {
    this.selectedIds.delete(unselectId);
    const newSelectedIds = [...this.selectedIds.values()];
    this.setSelectedIds(newSelectedIds);
    if (this.selectedIds.size > 0) {
      this.shiftSelectStartId = newSelectedIds[newSelectedIds.length - 1];
    } else {
      this.shiftSelectStartId = null;
    }
  };

  selectRow = (selectId: string): void => {
    if (!this.selectedIds.has(selectId)) {
      const newSelectedIds = this.selectedIds.add(selectId);
      this.shiftSelectStartId = selectId;
      this.setSelectedIds([...newSelectedIds]);
    }
  };

  handleContextMenuCall = (event: React.MouseEvent<HTMLDivElement>): void => {
    const element = targetElement(event.nativeEvent.target);
    if (!element) {
      return;
    }
    const id = findAncestorId(element);
    if (!id) {
      return;
    }
    event.preventDefault();
    if (!this.selectedIds.has(id)) {
      this.setSelectedRows([id]);
    }
  };

  isSelectionLocked = (): boolean => this.props.isSelectionLocked ?? false;

  handleDocumentMouseDown = (event: MouseEvent): void => {
    const appWrapper = document.getElementById('app_wrapper');
    let insideApp;
    if (!(event.target instanceof HTMLElement)) {
      return;
    }
    if (appWrapper) {
      insideApp = appWrapper.contains(event.target);
    }
    const { current: rootElement } = this.rootRef;
    if (!rootElement) {
      return;
    }
    // traverse up from root to first scrollable element
    const firstScrollable = getScrollableParent(rootElement);
    if (!firstScrollable) {
      return;
    }
    const outsideFirstScrollable =
      !firstScrollable.contains(event.target) &&
      firstScrollable !== event.target;
    if (insideApp && outsideFirstScrollable && !this.isSelectionLocked()) {
      this.setSelectedRows([]);
    }
  };

  handleDocumentKeyDown = (event: KeyboardEvent): void => {
    const { code, metaKey, ctrlKey } = event;

    if (!this.props.multiselect) {
      return;
    }
    if (!this.props.isSelectAllAllowed) {
      return;
    }
    const target = targetElement(event.target);
    if (!target) {
      return;
    }
    const focusOnInput = ['textarea', 'input'].includes(
      target.tagName.toLowerCase(),
    );
    const holdingCtrl =
      (getOS() === 'MacOS' && metaKey) || (getOS() !== 'MacOS' && ctrlKey);
    if (!focusOnInput && holdingCtrl && code === 'KeyA') {
      const { idKey = 'id' } = this.props;
      const allIds = this.props.items.map((item) => item[idKey]);
      this.setSelectedRows(allIds);
      event.preventDefault();
    }

    if (!focusOnInput && code === 'Escape') {
      this.props.onEscKeyPress?.();
      event.preventDefault();
    }
  };

  componentDidUpdate(prevProps: Props<Item>): void {
    const { ignoreIds = [], selectedIds } = this.props;
    const prevIgnoreIds = prevProps.ignoreIds ?? [];
    if (ignoreIds.length !== prevIgnoreIds.length) {
      this.setSelectedIds([]);
    }
    // in case we need to sync the state with a parent component
    if (selectedIds && selectedIds !== prevProps.selectedIds) {
      if (!isEqual(selectedIds, this.selectedIds)) {
        this.selectedIds = new Set(selectedIds);
      }
    }
  }

  componentDidMount(): void {
    document.addEventListener('mousedown', this.handleDocumentMouseDown);
    document.addEventListener('keydown', this.handleDocumentKeyDown);
  }

  componentWillUnmount(): void {
    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
  }

  render(): React.ReactNode {
    const { children, aria } = this.props;
    return (
      <div
        ref={this.rootRef}
        onMouseDown={this.handleMouseDown}
        onClick={this.handleOnClick}
        onContextMenu={this.handleContextMenuCall}
        className={this.props.className}
        role="listbox"
        tabIndex={-1}
        {...ariaToReactProps(aria)}
      >
        {children}
      </div>
    );
  }
}

export default SelectableItems;
